<script setup lang="ts">
import { ElConfigProvider, ElMessage } from 'element-plus'
import { ref, computed, onErrorCaptured, onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { Loading } from '@element-plus/icons-vue'
import { BYPASS_MODE, resolveDemoRole } from '@/config/demoRole'

// Theme configuration
const locale = ref(undefined)

const authStore = useAuthStore()
let sessionCheckInterval: any = null

const demoRoleLabel = computed(() => authStore.user?.role ?? resolveDemoRole())

onMounted(async () => {
  // Check authentication on app load
  await authStore.checkAuth()

  // Active session check function
  const verifyActiveSession = async () => {
    const isPublicPath = window.location.pathname.includes('/login') || window.location.pathname.includes('/callback') || window.location.pathname.includes('/unauthorized')
    if (!authStore.isAuthenticated && !isPublicPath) {
      console.warn('⏱️ Session expired or unauthenticated, redirecting to login')
      await authStore.logout()
      ElMessage.warning('La sessione è scaduta. Effettua nuovamente il login.')
      window.location.href = `/login?error=session_expired`
    }
  }

  // Periodic active check every 2 seconds to enforce instant session expiration
  sessionCheckInterval = setInterval(verifyActiveSession, 2000)

  // Also check whenever tab regains focus
  window.addEventListener('focus', verifyActiveSession)
})

onUnmounted(() => {
  if (sessionCheckInterval) {
    clearInterval(sessionCheckInterval)
  }
})

// Global error handler
onErrorCaptured((error: Error) => {
  console.error('Application error:', error)
  ElMessage.error({
    message: error.message || 'Si è verificato un errore imprevisto',
    duration: 5000
  })
  return false
})
</script>

<template>
  <ElConfigProvider :locale="locale">
    <!-- Non id="app": quello e' il punto di mount in index.html, e la regola
         sotto colpiva entrambi i div, raddoppiando il padding del banner. -->
    <div
      class="app-root"
      :class="{ 'has-demo-banner': BYPASS_MODE }"
    >
      <!-- Demo mode banner - unmissable warning -->
      <div
        v-if="BYPASS_MODE"
        class="demo-banner"
      >
        ⚠️ AMBIENTE DEMO — dati sintetici — ruolo: {{ demoRoleLabel }}
      </div>

      <!-- Global loading overlay during auth operations -->
      <div
        v-if="authStore.loading"
        class="auth-loading-overlay"
      >
        <el-icon
          class="is-loading"
          :size="40"
        >
          <Loading />
        </el-icon>
        <p>Verifica dell'accesso…</p>
      </div>

      <!-- Router view -->
      <router-view />
    </div>
  </ElConfigProvider>
</template>

<style>
/* On body, not on #app: toasts and message boxes are mounted on <body>,
   outside #app, and fell back to the browser's serif. */
body {
  font-family: var(--sm-font-ui);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Form controls don't inherit the font: every el-button was drawn in Arial. */
button,
input,
select,
textarea {
  font-family: inherit;
}

.app-root {
  height: 100vh;
  width: 100%;
  /* The banner is fixed, so it sits outside the flow and would otherwise cover
     the top bar (search field, avatar) and the sidebar logo. */
  padding-top: var(--sm-banner-h);
}

/* On :root, not on #app: overlays such as drawers are teleported to <body>,
   outside #app, and they need the same offset to clear the fixed banner. */
:root:has(.demo-banner) {
  --sm-banner-h: 34px;
}

/* The banner is fixed above everything, so a drawer opening at the top of the
   viewport would have its header hidden behind it. */
:root:has(.demo-banner) .el-drawer.rtl,
:root:has(.demo-banner) .el-drawer.ltr {
  top: var(--sm-banner-h);
  height: calc(100% - var(--sm-banner-h));
}

/* Anche i toast: a 16px dall'alto finivano sotto il banner, letti a meta'. */
:root:has(.demo-banner) .el-message {
  top: calc(var(--sm-banner-h) + 16px) !important;
}

/* Un pulsante grigio disabilitato aveva il testo appena piu' chiaro di uno
   attivo: sembrava cliccabile. I pulsanti colorati si schiariscono gia' da soli. */
.el-button.is-disabled:not(.el-button--primary):not(.el-button--danger):not(.el-button--success):not(.el-button--warning) {
  opacity: 0.55;
}

/* Heading typography */
h1, h2, h3, h4, h5, h6 {
  font-family: var(--sm-font-display);
  font-weight: 600;
  letter-spacing: -0.02em;
}

/* Global styles */
* {
  box-sizing: border-box;
}

body {
  margin: 0;
  padding: 0;
  background-color: var(--sm-bg);
  color: var(--sm-ink);
}

/* Utility classes */
.text-center {
  text-align: center;
}

.mt-1 { margin-top: 0.5rem; }
.mt-2 { margin-top: 1rem; }
.mt-3 { margin-top: 1.5rem; }
.mb-1 { margin-bottom: 0.5rem; }
.mb-2 { margin-bottom: 1rem; }
.mb-3 { margin-bottom: 1.5rem; }

/* Animation keyframes */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse-soft {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.animate-fade-in-up {
  animation: fadeInUp 0.4s ease-out forwards;
}

/* Demo mode banner */
.demo-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: var(--sm-banner-h);
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--sm-crit);
  color: var(--sm-on-primary);
  font-weight: bold;
  z-index: 9998;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}

/* Auth loading overlay */
.auth-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--sm-scrim-light);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
</style>
